import type {
  CheckoutRequest,
  CheckoutSuccessResult,
  CreditPackPurchaseResult,
  DownloadPurchaseResult,
  SubscriptionCheckoutResult,
} from '@Components/checkout-flow/checkout-flow.types';
import {
  CARD_UPDATE_SUCCESS_FREE_URI,
  CARD_UPDATE_SUCCESS_PREMIUM_URI,
  CheckoutMode,
  CREDIT_PACK_PURCHASE_SUCCESS_URI,
  PRICING_PAGE_PATH_SUFFIXES,
  SUBSCRIPTION_PROMOTION_CODE_COOKIE,
  SUBSCRIPTIONS_PAGE_URI,
} from '@Components/checkout-flow/checkout-flow.types';
import {isPremiumUser} from '@Utils/user.util';
import {trackDownloadPurchase, trackGA4Purchase} from '@Components/checkout-flow/checkout-flow-tracking.library';
import {deleteCookie} from '@Utils/cookie.util';
import {clearModalHistoryEntry} from '@Components/modal-container';
import {assertNever} from '@Utils/assert-never.util';

export interface CheckoutCompletionHandlers {
  showPremiumConfirmation: (result: SubscriptionCheckoutResult) => void;
  showError: (message: string) => void;
  exit: () => void;
}

interface CheckoutSuccessInput {
  request: CheckoutRequest;
  effectiveMode: CheckoutMode;
  result: CheckoutSuccessResult;
  handlers: CheckoutCompletionHandlers;
}

export const handleCheckoutSuccess = ({request, effectiveMode, result, handlers}: CheckoutSuccessInput): void => {
  if ('trialFailError' in result && result.trialFailError !== undefined && result.trialFailError !== '') {
    handlers.showError(result.trialFailError);
    return;
  }

  switch (effectiveMode) {
    case CheckoutMode.SUBSCRIPTION:
      completeSubscriptionPurchase(result, handlers);
      return;
    case CheckoutMode.CREDIT_PACK:
      completeCreditPackPurchase(request, result as CreditPackPurchaseResult, handlers);
      return;
    case CheckoutMode.DOWNLOAD:
      completeDownloadPurchase(request, result as DownloadPurchaseResult, handlers);
      return;
    case CheckoutMode.PAYMENT_PROCESSING:
    case CheckoutMode.PREMIUM_CONFIRMATION:
    case CheckoutMode.WELCOME_TO_TEAM:
      completeCardSetup();
      return;
    default:
      assertNever(effectiveMode);
  }
};

/** Only a completed purchase consumes the ambient promo code; clearing it any earlier would make a declined card lose it. */
const completeSubscriptionPurchase = (result: SubscriptionCheckoutResult, handlers: CheckoutCompletionHandlers): void => {
  deleteCookie(SUBSCRIPTION_PROMOTION_CODE_COOKIE);
  savePosterIfInEditor();
  handlers.showPremiumConfirmation(result);
};

const completeCreditPackPurchase = (request: CheckoutRequest, result: CreditPackPurchaseResult, handlers: CheckoutCompletionHandlers): void => {
  trackGA4Purchase(result.ga4PurchaseEventParams);

  if (request.mode === CheckoutMode.CREDIT_PACK && request.onSuccess !== undefined) {
    request.onSuccess(result);
    handlers.exit();
    return;
  }

  if (isPremiumUser()) {
    redirectTo(CREDIT_PACK_PURCHASE_SUCCESS_URI);
    return;
  }
  handlers.exit();
};

const completeDownloadPurchase = (request: CheckoutRequest, result: DownloadPurchaseResult, handlers: CheckoutCompletionHandlers): void => {
  trackGA4Purchase(result.ga4PurchaseEventParams);
  if ((result.orderValue ?? 0) > 0) {
    trackDownloadPurchase(result);
  }

  if (request.mode === CheckoutMode.DOWNLOAD && request.onSuccess !== undefined) {
    request.onSuccess(result);
  }
  handlers.exit();
};

const completeCardSetup = (): void => {
  redirectTo(isPremiumUser() ? CARD_UPDATE_SUCCESS_PREMIUM_URI : CARD_UPDATE_SUCCESS_FREE_URI);
};

const savePosterIfInEditor = (): void => {
  if (!window.PMW.isPosterMaker()) {
    return;
  }
  void window.posterEditor?.whiteboard?.savePoster.save();
};

interface CheckoutExitInput {
  request: CheckoutRequest;
  wasConfirmationShown: boolean;
}

/**
 * Leaving the "welcome to premium" confirmation reloads the page so the premium state (nav, credits, banners)
 * reflects the new subscription; on the pricing pages the billing page is the more useful destination.
 */
export const handleCheckoutExit = ({request, wasConfirmationShown}: CheckoutExitInput): void => {
  request.onClose?.();

  if (!wasConfirmationShown || request.mode === CheckoutMode.WELCOME_TO_TEAM) {
    return;
  }

  if (isOnPricingPage()) {
    redirectTo(SUBSCRIPTIONS_PAGE_URI);
    return;
  }
  clearModalHistoryEntry();
  window.location.reload();
};

const isOnPricingPage = (): boolean => {
  const currentPath = window.location.pathname;
  return PRICING_PAGE_PATH_SUFFIXES.some((suffix) => currentPath.endsWith(suffix));
};

/** Navigations from inside the modal must clear the modal's history entry first, or the modal middleware's queued history.back() cancels them. */
export const redirectTo = (uri: string): void => {
  clearModalHistoryEntry();
  window.location.href = window.PMW.util.site_url(uri);
};
